/**
 * MarkdownEditor — two-mode TipTap + textarea swap, persisting markdown
 * as the source of truth.
 *
 * Mode A (rich): TipTap WYSIWYG with a small toolbar above and a tiny
 *   text-link mode toggle below.
 * Mode B (markdown): plain monospace `<textarea>` with the same toggle
 *   below.
 *
 * Both modes edit the same underlying markdown string. The parent only
 * ever sees `value: string` (markdown) and `onChange(next: string)`.
 *
 * Toggle is asymmetric in design: rich → markdown is lossless (just
 * serialize), markdown → rich uses TipTap's parser. The ADR-003 allowed
 * node set round-trips cleanly per the spike test, so no warning UI is
 * shipped in v1; we'll add one later if a user reports loss.
 *
 * `onChange` is debounced 300ms while the user is editing in either
 * mode. External `value` changes (e.g. card refetched from the server)
 * sync into the editor only when they differ from the current internal
 * markdown — preventing user edits from being clobbered mid-typing.
 */

import type { MentionOptions } from '@tiptap/extension-mention'
import { TextSelection } from '@tiptap/pm/state'

import {
  BubbleMenu,
  type Editor,
  EditorContent,
  type Extension,
  type Mark,
  type Node as TiptapNode,
  useEditor,
} from '@tiptap/react'
import type { TFunction } from 'i18next'
import { MessageCircle, Quote } from 'lucide-react'
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { shouldShowFormattingMenu } from './formatting-menu-visibility'
import type { WorkItemAutodetectOptions } from './work-item-autodetect'

import { EmojiPickerPopover } from '@/features/chat/components/EmojiPickerPopover'

import { LinkEditorPopover } from './LinkEditorPopover'
import { toggleOrInsertCodeBlock } from './chat-code-block-keymap'
import { docBodiesEqual } from './doc-body-equality'
import { TableFooterPopover } from './nodes/table/TableFooterPopover'
import {
  isTableEdgeInView,
  tableMenuPopperOptions,
  tableMenuReferenceRect,
} from './nodes/table/active-table'
import { EditorToolbar } from './toolbar/EditorToolbar'
import { TextStyleDropdown } from './toolbar/TextStyleDropdown'
import { Divider, ToolbarButton } from './toolbar/ToolbarButton'
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BlockquoteIcon,
  BulletListIcon,
  CodeBlockIcon,
  ColDeleteIcon,
  ColLeftIcon,
  ColRightIcon,
  LinkIcon,
  OrderedListIcon,
  RowAboveIcon,
  RowBelowIcon,
  RowDeleteIcon,
  TableIcon,
  TaskListIcon,
} from './toolbar/icons'
import { type ToolbarPresetId, defaultPresetForSurface } from './toolbar/toolbar-presets'

import { buildEditorProseMirrorClass } from './editor-classes'
import { type EditorSurface, buildEditorExtensions } from './extensions'
import { captureSelectionAnchor, dispatchAddInlineComment } from './inline-comment-extension'
import {
  boldMarkdownHeadings,
  decidePasteStrategy,
  demoteHeadingsToBold,
  flattenTablesToParagraphs,
  promoteMonospaceSpansToCode,
  transformPastedHtmlForSchema,
} from './paste-strategy'
import { ViewModeCommentButton } from './view-mode-comment-button'

export { MarkdownEditorToolbar } from './toolbar/EditorToolbar'
export type { MarkdownEditorToolbarProps } from './toolbar/EditorToolbar'

export interface MarkdownEditorProps {
  /** Markdown source of truth. */
  value: string
  /** Debounced 300ms callback fired with the latest markdown string. */
  onChange?: (next: string) => void
  /** Placeholder shown when the editor is empty. (textarea only in v1.) */
  placeholder?: string
  /** Disable editing in both modes. */
  readOnly?: boolean
  /**
   * When true + readOnly, render the rendered markdown without the
   * bordered editor wrapper — just the prose content. Used by the card
   * detail modal so the description displays as plain text in display
   * mode and only switches to the bordered WYSIWYG when entering edit.
   */
  borderless?: boolean
  /** Accessible label for the editor surface. */
  ariaLabel?: string
  /** Initial mode; defaults to 'rich'. */
  initialMode?: 'rich' | 'markdown'
  /** Min visible rows for both surfaces. Default 3. */
  minRows?: number
  /** Max visible rows before scrolling. Default unbounded. */
  maxRows?: number
  /**
   * Optional keydown hook on the wrapper element. Mirrors the textarea
   * surface so callers (e.g. EditableField) can wire Cmd-Enter / Esc
   * commit shortcuts that work in both modes.
   *
   * IMPORTANT: bare `Enter` and `Shift+Enter` are FILTERED OUT before this
   * fires. In a multi-line editor those keys insert a paragraph or soft
   * break and must NOT bubble to a parent's "commit on Enter" handler
   * (otherwise typing newlines in a description saves the form). Only
   * `Cmd/Ctrl+Enter`, `Escape`, and other keys reach the caller.
   */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
  /**
   * Optional suggestion config for the @mention autocomplete (Phase 3b).
   * Build it with `buildMentionSuggestion({ getItems })` from
   * `src/features/mentions/mention-suggestion.ts`. Omit it on read-only
   * surfaces or in places where mentions are disabled (e.g. settings
   * forms) — without it, typing `@` is just typing `@`, no popover.
   */
  mentionSuggestion?: MentionOptions['suggestion']
  /**
   * Enables auto-detecting typed card references (`MOTE-30` + a space
   * becomes a work-item chip). Build it with `useWorkItemAutodetect()`
   * from `src/features/board/use-work-item-autodetect.ts`, which supplies
   * both halves (the tenant's project codes and the resolver).
   *
   * Lifecycle mirrors `mentionSuggestion` — captured ONCE at editor-init
   * time, so the option object holds GETTERS rather than values: project
   * codes are read at detection time, which is what lets the projects
   * query resolve after the editor has already mounted.
   *
   * Omit on read-only surfaces. The chip node itself is registered
   * unconditionally, so an existing chip renders and round-trips either
   * way — this only gates new detection.
   */
  workItemAutodetect?: WorkItemAutodetectOptions
  /**
   * Optional suggestion config for the `[[wikilink]]` autocomplete
   * (Wave C3). When set, typing `[[` opens a picker showing tenant
   * wiki nodes. Omit on surfaces that don't expose wikilink authoring
   * — the markdown round-trip is unconditional, so existing wikilinks
   * still render + serialize correctly on every surface; this prop
   * only gates the picker UI on insert. Today only the Wiki doc page
   * wires it; comments + card descriptions + chat composer leave it
   * undefined.
   *
   * Lifecycle mirrors `mentionSuggestion` exactly — captured ONCE at
   * editor-init time (via the deps-empty `useEditor` call below). The
   * suggestion plugin is wired into ProseMirror state and doesn't
   * support hot-swapping its config; rebuild the parent if the search
   * source needs to change mid-session.
   */
  wikilinkSuggestion?: MentionOptions['suggestion']
  /**
   * Whether to render the table tools (Table extension + the floating
   * `<TableBubbleMenu>`). Default true — full-editor surfaces (card
   * description) get tables. Short-form surfaces (`CommentComposer`,
   * comment inline-edit) pass `false` to skip both — fixes a tippy.js
   * unmount race where clearing the editor on submit crashes the app
   * with `Failed to execute 'removeChild' on 'Node'`. R-2 caught this
   * across Phases 2d / 3b / 3c.
   */
  enableTables?: boolean
  /**
   * Let a checkbox click stand while `readOnly` is true, rather than
   * TipTap reverting it. Opt-in, because the default is right for any
   * surface with no persistence path (a display-mode card description
   * would otherwise tick a box nothing saves).
   *
   * This only stops the revert — it does NOT persist. The surface that
   * sets it also owns a listener that resolves which item was clicked
   * and writes the change (`useViewModeTaskToggle` on the wiki doc page).
   */
  allowReadOnlyTaskToggle?: boolean
  /**
   * Focus the editor on mount. Used by the collapsible CommentComposer
   * — when the user clicks the placeholder, the editor mounts already
   * focused so they can start typing immediately.
   */
  autoFocus?: boolean
  /**
   * Files picked through the toolbar's Attach button. The surface routes
   * them down the SAME path an OS-file drop takes (validation, toasts,
   * inline placeholders, upload, refetch) so click-to-attach and drag-to-
   * attach can never drift. When omitted, the Attach item does not render
   * — a surface with no persistence path shows no dead button.
   */
  onAttachFiles?: (files: File[]) => void
  /**
   * Phase 4a (chat composer) — when true, do NOT filter bare `Enter` /
   * `Shift+Enter` from the parent's `onKeyDown`. Default `false` (the
   * existing description / comment surfaces want bare Enter to insert a
   * paragraph, never to commit). The chat composer flips this to `true`
   * so it can interpret bare `Enter` as "send" and let TipTap handle
   * `Shift+Enter` for soft breaks.
   */
  forwardEnterKeys?: boolean
  /**
   * Phase 4a (chat composer) — when false, hide the rich/markdown mode
   * toggle UI. Chat is markdown-only by design (legacy parity), so the
   * composer suppresses the toggle. Defaults to `true` to preserve every
   * other call-site (description, comments, etc.).
   */
  enableModeToggle?: boolean
  /**
   * Phase 4d — extra TipTap extensions appended after the canonical set.
   * Used by the chat composer + chat read-view to add a second `Mention`
   * extension instance (`channelMention`, trigger `#`) alongside the
   * default `@user` mention. See `BuildEditorExtensionsOptions.extraExtensions`
   * for the full pitfall doc.
   *
   * The reference is captured ONCE on first mount (deps-empty `useEditor`
   * call below) — same as `mentionSuggestion`. Mutating the array later
   * has no effect; remount the editor's parent to swap the extra set.
   */
  extraExtensions?: ReadonlyArray<Extension | TiptapNode | Mark>
  /**
   * Exposes the underlying TipTap editor instance to the
   * parent so external surfaces (e.g. the chat composer's
   * `<EmojiPickerPopover>`) can call `editor.commands.insertContent(...)`
   * at the cursor. Fires once when the editor is created; the parent
   * should hold the reference in a ref since it's stable for the
   * component's lifetime (deps-empty `useEditor` call). Pass `null` is
   * not emitted on unmount — TipTap's view destruction is handled
   * internally and the parent is expected to drop its reference when
   * the component unmounts.
   */
  onEditorReady?: (editor: Editor) => void
  /**
   * When false, suppress the inline rich-mode formatting
   * toolbar (bold / italic / lists / link / code block / table / image /
   * emoji). The chat composer flips this to false because it renders
   * its OWN bottom toolbar with the +/@/emoji/Aa controls (legacy
   * parity, see `Composer.jsx`'s `<ChatBottomToolbar>`). Defaults to
   * `true` for every other surface (description, comments, etc.).
   */
  enableFormattingToolbar?: boolean
  /**
   * Which toolbar preset to render (`src/editor/toolbar/toolbar-presets.ts`):
   * the ordered item set, collapse priorities, and menu-only items for this
   * surface. Defaults from `surface` (card → `card`, comment → `comment`,
   * wiki → `wiki`, chat → `chat`); the wiki comment composers pass
   * `'wiki-comment'` explicitly through `<MarkdownEditorToolbar bare>`.
   */
  toolbarPreset?: ToolbarPresetId
  /**
   * Phase 1 Wiki — opt-in syntax highlighting for fenced code blocks
   * via lowlight. The extension + ~50 KB of language grammars are
   * dynamically loaded from `./code-highlight-extension.ts` ONLY when
   * this prop is true, so chat/comments/board surfaces don't pay the
   * bundle cost. When true, the editor mount briefly waits on the
   * dynamic import before rendering content; a 1-tick skeleton state
   * keeps the visual stable.
   *
   * Markdown round-trip safety is unchanged — lowlight is a presentation
   * extension layered on the existing CodeBlock node; `tiptap-markdown`
   * still serializes to standard fenced-code blocks (` ```ts ... ``` `).
   * ADR-003 holds.
   */
  enableCodeHighlight?: boolean
  /**
   * Chrome variant. `'card'` (default) keeps the bordered + elevated
   * wrapper — the contained-box look used by chat composers, comment
   * editors, and card descriptions, where the editor needs to read as
   * a distinct surface inside a denser layout.
   *
   * `'flush'` (Wiki doc page) drops the outer border, the elevated
   * background, and the shadow so the editor sits flat on the page
   * background — the Confluence / Notion full-page editor look. The
   * toolbar still renders as a thin horizontal bar above the body but
   * with no enclosing frame, and the rich/markdown mode toggle row at
   * the bottom is suppressed entirely (the wiki doc page exposes that
   * affordance through its own overflow menu so the editor surface
   * itself stays uncluttered).
   *
   * Read-only renders ignore the variant — both modes already collapse
   * to a plain `<EditorContent>` for prose display.
   */
  chrome?: 'card' | 'flush'
  /**
   * Optional controlled rich/markdown mode. When provided the editor
   * defers to the parent for the active surface; the internal
   * `initialMode` state still seeds the first render but external
   * updates after that win. Used by the Wiki doc page so the chrome's
   * overflow menu (Edit as markdown / Edit as rich text) can drive the
   * editor — leaving the editor surface itself frame-free per the
   * flush-chrome design.
   *
   * Omit on every other call-site; the editor's internal switch button
   * (rendered at the bottom of the card chrome) keeps working as
   * before.
   */
  markdownMode?: 'rich' | 'markdown'
  /**
   * Fires whenever the active surface flips (either via the internal
   * toggle button on the card chrome, OR — when `markdownMode` is the
   * controlled prop — when the controlled value changes). Lets the
   * parent mirror state without polling. Paired with `markdownMode`
   * for a fully-controlled flow.
   */
  onModeChange?: (mode: 'rich' | 'markdown') => void
  /**
   * When false, suppress the inline toolbar render entirely so the
   * parent can render `<MarkdownEditorToolbar editor={editor} />`
   * elsewhere on the page (e.g. the Wiki doc's full-width sticky
   * toolbar row above the title). The editor still exposes its
   * instance through `onEditorReady`. Defaults to `true` so every
   * existing chat / comment / card-description call-site keeps the
   * inline toolbar.
   *
   * Independent of `enableFormattingToolbar` (which kills the toolbar
   * for both inline AND external surfaces); use `showInlineToolbar`
   * when you want the toolbar to live, just somewhere else.
   */
  showInlineToolbar?: boolean
  /**
   * Phase 4a — caller surface tag. Default `'card'` covers every
   * legacy call-site (description, comment, chat). Wiki doc page
   * passes `'wiki'`, which:
   *   - registers the slash-command extension (`/`-triggered block menu),
   *   - lets the popover suggestion config land via `slashCommandSuggestion`.
   *
   * Captured ONCE at editor-init (deps-empty `useEditor`), like
   * `mentionSuggestion` and `wikilinkSuggestion` — see the canonical
   * comment on the `useEditor` call for why.
   */
  surface?: EditorSurface
  /**
   * Phase 4a — slash-menu `@tiptap/suggestion` config. Built via
   * `buildSlashCommandSuggestion({ commands })` from
   * `src/features/wiki/lib/slash-command-suggestion.ts`. No-op when
   * `surface !== 'wiki'` since the extension itself is gated on
   * surface inside `buildEditorExtensions`.
   */
  slashCommandSuggestion?: MentionOptions['suggestion']
  /**
   * Phase 4a — getter for the localized "Type / to insert a block"
   * hint rendered on every non-first empty paragraph in the wiki
   * editor. Threaded as a function (not a static string) so a locale
   * switch propagates without rebuilding the editor — the underlying
   * extension re-invokes it on every plugin tick. No-op when
   * `surface !== 'wiki'`.
   */
  getSlashHintTranslation?: () => string
  /**
   * Selection bubble menu preset. `'default'` keeps the current behavior
   * (table bubble menu only when `enableTables` is true). `'comment'`
   * renders a slim 5-button selection bubble (Bold / Italic / Strike /
   * Link / Inline code) for short-form prose; the table bubble menu is
   * suppressed regardless of `enableTables`. `'none'` skips every bubble
   * menu.
   */
  bubbleMenuPreset?: 'default' | 'comment' | 'none'
  /**
   * Phase 4 — value-string encoding. Default `'markdown'`
   * matches every legacy surface (chat composer, card description,
   * comments, ticket markdown). Wiki passes `'json'` to swap the
   * setContent + onChange paths:
   *
   *   - `'markdown'` (default): `value` is markdown text; setContent
   *     uses tiptap-markdown's parser, onChange emits the markdown
   *     storage's `getMarkdown()`.
   *   - `'json'`: `value` is a `JSON.stringify`'d TipTap ProseMirror
   *     doc; setContent uses the parsed object directly, onChange emits
   *     `JSON.stringify(editor.getJSON())`.
   *
   * Markdown paste still works under `'json'` — `tiptap-markdown`'s
   * paste handler stays registered on every surface; only the SAVE
   * serialization branch differs. (Confluence parity per Phase 4 plan.)
   *
   * Dual-format read (canonical-JSON migration, ADR-039): under `'json'`
   * a `value` that does NOT parse as a serialized doc is treated as a
   * legacy MARKDOWN body and routed through tiptap-markdown's parser —
   * cards + comments carry both row formats on the wire during the
   * migration window. Any edit then serializes as JSON on save (lazy
   * write-coercion). The rich/markdown mode toggle is suppressed under
   * `'json'` — the textarea edits markdown source, which is no longer a
   * persistable encoding on those surfaces.
   *
   * Captured ONCE at editor-init time (deps-empty `useEditor`) — flipping
   * mid-life would race the BubbleMenu tippy.js lifecycle; consumers
   * that need to swap encodings remount the parent.
   */
  valueFormat?: 'markdown' | 'json'
  /**
   * The AUTHORITATIVE stored format of the incoming `value`, from the
   * wire's `descriptionFormat` / `bodyFormat` discriminator. Only
   * consulted under `valueFormat='json'`: `'markdown'` disables the
   * JSON-parse branch on the read path entirely, so a legacy markdown
   * row whose literal text happens to parse as a ProseMirror doc (a user
   * pasted raw `{"type":"doc",...}` pre-migration) renders as the text
   * it is instead of being hydrated into rich content. When omitted the
   * read path falls back to shape self-discrimination (rollout safety
   * for payloads that don't carry the discriminator yet).
   */
  contentFormat?: 'markdown' | 'json'
  /**
   * Wiki inline-comments (ADR-037) — gates the bubble-menu "Comment"
   * button visibility. Only relevant when `surface === 'wiki'`; the
   * button is hidden when this is `false` (Reader / view-only-on-doc
   * users). Defaults to `false` so the button never surfaces unless the
   * caller (the wiki doc page) explicitly opts in by threading the
   * resolved per-doc `canComment` permission. Other surfaces leave it
   * unset and the button is never rendered (it's surface-gated too).
   */
  canComment?: boolean
}

const DEBOUNCE_MS = 300

/**
 * Compute approximate min/max heights from row counts. Uses 1.5rem
 * line height as a baseline plus the standard `px-3 py-2` padding.
 */
function rowToPx(rows: number): number {
  // 1.5rem ≈ 24px line-height + 16px combined vertical padding
  return rows * 24 + 16
}

/**
 * Read the current markdown from a TipTap editor. Returns '' if the
 * editor is null or the markdown storage isn't ready.
 */
/**
 * Which tree shape hosts `<EditorContent>`. Exported for the regression
 * test that pins the freeze; the component calls it once per render and
 * compares the answer against the shape it committed to at mount.
 */
export type EditorContentShape = 'readOnlyBare' | 'readOnlyBoxed' | 'editable'

export function resolveContentShape(props: {
  readOnly?: boolean
  surface?: EditorSurface
  borderless?: boolean
  chrome?: 'card' | 'flush'
}): EditorContentShape {
  if (!props.readOnly || props.surface === 'wiki') return 'editable'
  return props.borderless || props.chrome === 'flush' ? 'readOnlyBare' : 'readOnlyBoxed'
}

function readMarkdown(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return ''
  const storage = editor.storage as { markdown?: { getMarkdown: () => string } }
  return storage.markdown?.getMarkdown() ?? ''
}

/**
 * Read the current TipTap ProseMirror doc as a JSON string. Used by
 * the Phase 4 wiki surface, which persists JSON instead of markdown so
 * slash-only blocks (panels, expand, status/date chips, work-item
 * embed, video, file) survive a save round-trip. `JSON.stringify` keeps
 * the storage shape a string at the prop boundary — same `value: string`
 * surface as the markdown path; the consumer parses on its side.
 */
function readJsonString(editor: ReturnType<typeof useEditor>): string {
  if (!editor) return ''
  try {
    return JSON.stringify(editor.getJSON())
  } catch {
    return ''
  }
}

/**
 * Hand the right "what's currently in the editor as a string" out per
 * `valueFormat`. Centralized so the onUpdate / unmount-flush / dep-sync
 * paths all read through one helper instead of duplicating the branch.
 *
 * Exported for the rich-text JSON save spec, which pins the doc a
 * card-surface editor emits against the shared per-surface whitelist
 * fixture (`richtext-surfaces.json`).
 */
export function readEditorValue(
  editor: ReturnType<typeof useEditor>,
  valueFormat: 'markdown' | 'json',
): string {
  return valueFormat === 'json' ? readJsonString(editor) : readMarkdown(editor)
}

/**
 * Do two serialized editor values MEAN the same thing?
 *
 * Never compare editor values bytewise. A body that round-trips through
 * the BE comes back with its object keys reordered — bodies are stored in
 * a `jsonb` column, which canonicalizes keys by length-then-bytewise, so
 * a text node TipTap emitted as `{type, text}` returns as `{text, type}`.
 * Layer on the paragraph-id back-fill and TipTap's default-attr
 * materialization and a body nobody touched is GUARANTEED to differ
 * byte-for-byte from the one the editor is holding.
 *
 * JSON surfaces therefore compare through `docBodiesEqual` (key order,
 * volatile paragraph ids, in-flight upload placeholders, and the several
 * encodings of "empty" all normalize away). Markdown surfaces have no
 * equivalent normalizer and keep byte equality — `docBodiesEqual` would
 * degrade to exactly that on a parse failure, but branching states the
 * intent rather than leaning on a fallback a future edit could change.
 *
 * Exported for the caret-preservation spec, same as `readEditorValue`.
 */
export function valuesEquivalent(a: string, b: string, valueFormat: 'markdown' | 'json'): boolean {
  if (a === b) return true
  return valueFormat === 'json' && docBodiesEqual(a, b)
}

/**
 * Put the caret back after a whole-document replacement.
 *
 * `setContent` issues `replaceWith(0, doc.content.size, …)` and sets no
 * selection of its own, so ProseMirror maps every position inside the
 * replaced range to the END of the new content: the caret lands at the
 * bottom of the document. That is the entire mechanism behind "I randomly
 * get sent to the end of the document and my typing continues from
 * there".
 *
 * After a GENUINE remote change the pre-replace offsets are approximate —
 * but landing near where the user was beats the bottom of the page in
 * every case. `TextSelection.between` resolves to the nearest valid text
 * position, so an offset that now falls inside an atom (an image, a
 * work-item chip) or past a shortened document degrades to the closest
 * caret instead of throwing.
 *
 * Only called when the editor genuinely had focus — restoring a selection
 * into an unfocused editor would steal the caret from wherever the user
 * actually is.
 *
 * Exported for the caret-preservation spec, same as `readEditorValue`.
 */
export function restoreSelectionAfterReplace(editor: Editor, from: number, to: number): void {
  const { doc } = editor.state
  const max = doc.content.size
  const $from = doc.resolve(Math.min(from, max))
  const $to = doc.resolve(Math.min(to, max))
  // `scrollIntoView` because `setContent` will have scrolled to wherever
  // it dumped the caret — on a long doc, restoring the position without
  // it leaves the user correctly placed but looking at the wrong part of
  // the page.
  editor.view.dispatch(
    editor.state.tr.setSelection(TextSelection.between($from, $to)).scrollIntoView(),
  )
}

/**
 * Parse a JSON value-string into a TipTap content object suitable for
 * `editor.commands.setContent(...)`. Empty + parse-failure cases
 * collapse to `null` so the caller can pass a literal empty paragraph
 * (TipTap accepts `null` / `''` and renders an empty doc either way).
 * Requires an object carrying a string `type` (the doc root) — a bare
 * JSON scalar or array is not a ProseMirror document and falls through
 * to `null` like any other non-doc string.
 */
function parseJsonValue(value: string): Record<string, unknown> | null {
  if (value.length === 0) return null
  try {
    const parsed: unknown = JSON.parse(value)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { type?: unknown }).type === 'string'
    ) {
      return parsed as Record<string, unknown>
    }
    return null
  } catch {
    return null
  }
}

/**
 * Resolve the `setContent` / init-content argument for a
 * `valueFormat='json'` surface. A serialized doc parses into the object
 * TipTap consumes directly; any other non-empty string is a legacy
 * MARKDOWN body (cards + comments are dual-format on the wire during
 * the canonical-JSON migration window) and is returned verbatim so
 * tiptap-markdown's parser handles it — the exact read path those rows
 * had before the surface flipped to JSON saves. Once such a doc is
 * edited, `onUpdate` emits JSON, so the row upgrades on its next save
 * (lazy write-coercion).
 *
 * Exported for the format-discriminator spec, which pins that a
 * markdown-format row is never JSON-parsed.
 */
export function resolveJsonContent(
  value: string,
  contentFormat?: 'markdown' | 'json',
): Record<string, unknown> | string {
  if (value.length === 0) return ''
  // The wire discriminator is authoritative when present: a
  // markdown-format row is NEVER JSON-parsed, even if its literal text
  // would parse as a doc (pathological pre-migration bodies).
  if (contentFormat === 'markdown') return value
  return parseJsonValue(value) ?? value
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  readOnly = false,
  borderless = false,
  ariaLabel,
  initialMode = 'rich',
  minRows = 3,
  maxRows,
  onKeyDown,
  mentionSuggestion,
  workItemAutodetect,
  wikilinkSuggestion,
  enableTables = true,
  allowReadOnlyTaskToggle = false,
  autoFocus = false,
  onAttachFiles,
  forwardEnterKeys = false,
  enableModeToggle = true,
  extraExtensions,
  onEditorReady,
  enableFormattingToolbar = true,
  toolbarPreset,
  enableCodeHighlight = false,
  chrome = 'card',
  markdownMode,
  onModeChange,
  showInlineToolbar = true,
  surface = 'card',
  slashCommandSuggestion,
  getSlashHintTranslation,
  valueFormat = 'markdown',
  contentFormat,
  bubbleMenuPreset = 'default',
  canComment = false,
}: MarkdownEditorProps) {
  const { t } = useTranslation('common')
  // Internal mode state is the source of truth when `markdownMode` is
  // omitted; when the parent supplies the controlled prop, `mode`
  // below derives from it and the effect further down pushes the
  // markdown→rich `setContent` flip into the editor.
  const [internalMode, setInternalMode] = useState<'rich' | 'markdown'>(initialMode)
  const mode: 'rich' | 'markdown' = markdownMode ?? internalMode

  /*
   * Phase 1 Wiki — when `enableCodeHighlight` is true, dynamic-load the
   * lowlight extension and gate the editor mount on it. The extension +
   * 10 language grammars (~15 KB brotli) is the load-bearing dep
   * keeping the bundle cost OUT of the main bundle for every other
   * `<MarkdownEditor>` consumer (chat, comments, ticket descriptions —
   * none of which want code highlighting).
   *
   * TipTap's `useEditor` is deps-empty by design (re-init causes a
   * tippy.js unmount race in BubbleMenu — see the canonical comment on
   * the `useEditor` call below). To avoid that, we hold the editor's
   * mount in a "loading" state until the dynamic import resolves; the
   * wrapper div remounts via a `key` derived from the extension's
   * presence, which gives us a clean re-init the first (and only) time
   * the extension lands. For surfaces that don't opt in
   * (`enableCodeHighlight === false` — the default), this branch is a
   * no-op and the editor mounts on first render exactly as before.
   */
  const [codeHighlightExtension, setCodeHighlightExtension] = useState<Extension | null>(null)
  useEffect(() => {
    if (!enableCodeHighlight) return
    let cancelled = false
    void import('./code-highlight-extension').then(({ buildCodeHighlightExtension }) => {
      if (cancelled) return
      setCodeHighlightExtension(buildCodeHighlightExtension() as unknown as Extension)
    })
    return () => {
      cancelled = true
    }
  }, [enableCodeHighlight])

  /*
   * Compose the final extras: caller-supplied `extraExtensions` + (when
   * present) the dynamically-loaded code-highlight extension. The
   * resulting array flows into the deps-empty `useEditor` call below —
   * captured at the first mount with the value present (because we
   * gate the early-return on the extension when `enableCodeHighlight`
   * is true).
   */
  const composedExtras = useMemo<ReadonlyArray<Extension | Mark | TiptapNode> | undefined>(() => {
    if (!codeHighlightExtension && !extraExtensions) return undefined
    const base = extraExtensions ?? []
    if (codeHighlightExtension) return [...base, codeHighlightExtension]
    return base
  }, [codeHighlightExtension, extraExtensions])
  // The canonical markdown the parent will eventually observe via
  // `onChange`. We keep this in state so the textarea has a controlled
  // value across mode swaps.
  const [markdown, setMarkdown] = useState<string>(value)

  // Debounce timer for `onChange`. Stored in a ref so re-renders don't
  // restart it.
  /**
   * Which of the three tree shapes below hosts `<EditorContent>`, decided
   * ONCE and then frozen for this component's life.
   *
   * The shapes are genuinely different positions in the React tree: a bare
   * `<EditorContent>`, one wrapped in a bordered box, and the editable
   * branch's several nested wrappers. Letting `readOnly` (or `surface` /
   * `borderless` / `chrome`) move between them mid-life unmounts the live
   * `<EditorContent>` and mounts a new one against the SAME editor, and
   * TipTap's `PureEditorContent` cannot survive that: the incoming
   * constructor runs in the render phase, sees the outgoing instance's
   * still-assigned `editor.contentComponent`, and records the node-view
   * portal store as already initialized; the outgoing unmount then nulls
   * the field; the incoming `init()` mints a FRESH store and, because that
   * flag is already set, neither subscribes to it nor schedules a render.
   * The committed `<Portals>` stays bound to the orphaned store forever, so
   * every node view registered afterwards paints into nothing — an attached
   * image / file / video lands in the document and uploads, but the author
   * sees an empty box, no error, and a submit button held by a row they
   * cannot reach. Freezing the shape is what makes that unreachable.
   *
   * This is the general form of the rule the wiki surface already gets by
   * being excluded from the read-only fast path below.
   */
  const contentShapeRef = useRef<'readOnlyBare' | 'readOnlyBoxed' | 'editable' | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Tracks the latest committed-to-parent markdown so we can suppress
  // duplicate emissions when the parent re-renders us with the same
  // value.
  const lastEmittedRef = useRef<string>(value)

  // Mirrors the latest `markdown` state into a ref so the unmount flush
  // can read it without depending on `markdown` (which would re-bind the
  // cleanup on every keystroke and defeat the unmount-only intent).
  const markdownRef = useRef<string>(value)
  useEffect(() => {
    markdownRef.current = markdown
  }, [markdown])

  // Same trick for `onChange` — the unmount flush needs the latest
  // callback without re-binding on every render.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Latest `onKeyDown` + `forwardEnterKeys` for the ProseMirror-level
  // Enter intercept in `editorProps.handleKeyDown` (built once inside the
  // deps-empty `useEditor` call below). Refs keep the closure current
  // without re-initing the editor.
  const onKeyDownRef = useRef(onKeyDown)
  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  }, [onKeyDown])
  const forwardEnterKeysRef = useRef(forwardEnterKeys)
  useEffect(() => {
    forwardEnterKeysRef.current = forwardEnterKeys
  }, [forwardEnterKeys])

  const fireChange = useCallback(
    (next: string) => {
      setMarkdown(next)
      // Advance the mirror SYNCHRONOUSLY, ahead of the `[markdown]`
      // effect below. That effect lands a commit later, and the gap is
      // reachable: the debounced `onChange` fires from a timer, so the
      // parent's echo can arrive as a `value` prop while a keystroke that
      // landed in the same tick is still only in React state. The sync
      // gate would then compare the parent's (correct, slightly older)
      // string against a STALE ref, mismatch, and replace the live
      // document — losing the newest character and the caret with it.
      // The effect stays as the backstop for the non-typing writers
      // (`switchMode`, the controlled-mode bridge) that call `setMarkdown`
      // directly.
      markdownRef.current = next
      if (!onChange) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        if (next !== lastEmittedRef.current) {
          lastEmittedRef.current = next
          onChange(next)
        }
      }, DEBOUNCE_MS)
    },
    [onChange],
  )

  // Build the TipTap editor lazily based on current mode. We always
  // create the editor (even in markdown mode) and just hide it — that
  // way switching modes is a near-instant DOM swap and the editor
  // parses the markdown once on mount.
  //
  // The mention suggestion config is captured ONCE at editor-init time
  // (via the deps array on `useEditor`) — not on every prop change. The
  // suggestion plugin is wired into ProseMirror state and doesn't
  // support hot-swapping its config. Callers that need to swap the
  // member list (e.g. project switch) get a re-init via the readOnly
  // dep below; in practice the mention list is stable across the life
  // of a single card-detail modal.
  const editor = useEditor(
    {
      // `immediatelyRender: false` defers the first ProseMirror view
      // render to a `useEffect` rather than running it inside the React
      // render cycle. Without it, TipTap's `PureEditorContent` calls
      // `flushSync` during its initial render — React 18 strict mode +
      // concurrent rendering surface this as a console warning
      // ("flushSync was called from inside a lifecycle method"). Per
      // the TipTap docs this is the recommended setting for React 18+;
      // the trade-off is one extra frame of "empty editor" before the
      // body paints, which is invisible in practice.
      immediatelyRender: false,
      extensions: buildEditorExtensions({
        mentionSuggestion,
        workItemAutodetect,
        wikilinkSuggestion,
        enableTables,
        allowReadOnlyTaskToggle,
        extraExtensions: composedExtras ?? extraExtensions,
        placeholder,
        surface,
        slashCommandSuggestion,
        getSlashHintTranslation,
      }),
      // For `valueFormat='json'`, `value` is a JSON.stringify'd
      // ProseMirror doc — parse it on init so TipTap mounts against the
      // doc shape directly (no markdown-it round-trip). A non-doc string
      // under `'json'` is a legacy markdown row (dual-format read) and
      // falls through to tiptap-markdown's parser. Markdown path
      // (default) passes the raw string through; tiptap-markdown parses
      // it on init.
      content: valueFormat === 'json' ? resolveJsonContent(value, contentFormat) : value,
      editable: !readOnly,
      editorProps: {
        // Wiki docs are long scrolling pages — keep the caret away from
        // the scrollport's bottom edge while typing (Notion / Confluence
        // behavior). When the caret comes within `scrollThreshold` px of
        // the edge, ProseMirror scrolls it back to `scrollMargin` px of
        // breathing room. PM's caret auto-scroll ignores CSS
        // scroll-padding, so editorProps is the only lever. Pairs with
        // the edit-mode `padding-bottom: 30vh` on the wiki ProseMirror
        // (global.css), which provides the scrollable room below the
        // last line.
        //
        // Max-height composers (chat + message edit — anything with
        // `maxRows`) get a SMALL margin: PM's default scroll math
        // undershoots when the caret lands in a fresh EMPTY paragraph
        // (degenerate caret rect), so the ArrowDown code-block exit at
        // max height left the new line hidden below the fold (staging
        // finding). 24px of headroom absorbs the measurement slack on
        // every scroll-into-view path. Other surfaces (card / comment
        // without maxRows) are short editors inside modals where a
        // margin would cause scroll jumps — they keep PM defaults.
        ...(surface === 'wiki'
          ? { scrollThreshold: 80, scrollMargin: 160 }
          : maxRows
            ? { scrollThreshold: 24, scrollMargin: 24 }
            : {}),
        attributes: {
          // `borderless` (used in card-modal display mode) strips the
          // editor-internal padding so the rendered content aligns with
          // other EditableField rows and doesn't compound vertical
          // padding from the parent's hover wrapper.
          //
          // `chrome === 'flush'` (Wiki) also strips the horizontal
          // padding so the prose left edge sits flush with the title
          // input above it (the parent owns the page-level gutter). It
          // keeps a small vertical pad so the first paragraph doesn't
          // bump against the toolbar / title spacing. The prose size
          // also bumps to `prose-base` so heading + body sizes feel
          // appropriate for a full-page doc editor (Notion-feel) — the
          // dense `prose-sm` is right for chat / cards but reads tight
          // on a 760px-wide page.
          // Composition (incl. the `wiki-doc-surface` scoping hook) lives
          // in `editor-classes.ts` so it can be unit-tested on its own.
          class: buildEditorProseMirrorClass({ surface, chrome, borderless }),
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        },
        // Rewrites applied before ProseMirror parses a pasted fragment.
        // The rules live in `paste-strategy.ts` so this editor and the
        // specs that exercise paste share one implementation — the
        // transform is an editor PROP, so a harness that builds an
        // editor from extensions alone silently skips it.
        transformPastedHTML: (html, view) => {
          const { nodes } = view.state.schema
          return transformPastedHtmlForSchema(html, {
            hasTable: Boolean(nodes.table),
            hasHeading: Boolean(nodes.heading),
          })
        },
        // A plain-text-only paste never reaches `transformPastedHTML` —
        // tiptap-markdown parses the text straight to a slice. Rewrite
        // `#` headings to bold here, before that parse, for the same
        // reason as above. `inCodeOrPlain` covers both a caret inside a
        // code block and an explicit paste-as-plain-text; markdown is
        // not parsed in either case, so neither should this be.
        transformPastedText: (text, inCodeOrPlain, view) => {
          if (inCodeOrPlain || view.state.schema.nodes.heading) return text
          return boldMarkdownHeadings(text)
        },
        // `forwardEnterKeys` surfaces (chat composer, message edit)
        // interpret bare Enter as send/save. That decision MUST run at
        // the ProseMirror layer, BEFORE TipTap's Enter keymap: PM's
        // native keydown listener on the contenteditable fires before
        // React's root-delegated wrapper `onKeyDown`, so a wrapper-level
        // handler sees the event only AFTER the paragraph split already
        // landed — the cursor visibly jumped to a new line for a frame
        // before the send cleared it. Direct `editorProps` take
        // precedence over plugin keymaps, so a `true` here prevents the
        // split entirely. The parent's handler keeps its contract: call
        // `preventDefault()` to consume the key (send/save), or return
        // without it to let TipTap proceed (Shift+Enter soft break,
        // code-block newline, mention-popover accept — those bail-outs
        // return `false` here and fall through to the plugins).
        handleKeyDown: (_view, event) => {
          if (!forwardEnterKeysRef.current) return false
          if (event.key !== 'Enter') return false
          const handler = onKeyDownRef.current
          if (!handler) return false
          // The parent handlers only read key/shiftKey/metaKey/ctrlKey +
          // call preventDefault/stopPropagation — all present on the
          // native event; the React-synthetic type is a formality here.
          handler(event as unknown as KeyboardEvent<HTMLDivElement>)
          return event.defaultPrevented
        },
        // A clipboard payload carries the same selection twice — rich
        // (`text/html`) and flat (`text/plain`). `decidePasteStrategy`
        // owns which half to parse; see `paste-strategy.ts` for the
        // rule. The only case handled here is "the HTML is a bare
        // wrapper around markdown SOURCE" — everything else falls
        // through to ProseMirror's own pipeline.
        handlePaste: (view, event) => {
          // ProseMirror re-enters this handler from `doPaste` with a
          // synthetic event that carries no payload — nothing to decide.
          const clipboardData = (event as ClipboardEvent | null)?.clipboardData
          if (!clipboardData) return false
          const plain = clipboardData.getData('text/plain')
          const html = clipboardData.getData('text/html')
          if (decidePasteStrategy(html, plain) !== 'markdown') return false
          // Reproduce ProseMirror's own text pipeline in order:
          // `transformPastedText` first, then `clipboardTextParser`.
          //
          // Running the transform is not optional. Skipping it made this
          // branch silently diverge from a plain-text-only paste of the
          // SAME content — a heading demoted to bold when the clipboard
          // carried only `text/plain`, but stayed unmarked when it also
          // carried a `<pre>` half, because only the former went through
          // ProseMirror's pipeline. Any future text-level rewrite would
          // inherit the same split.
          let text = plain
          view.someProp('transformPastedText', (f) => {
            text = f(text, false, view)
          })
          // Hand the result to tiptap-markdown's OWN
          // `clipboardTextParser` with `plainText: false` — the exact
          // path a plain-text-only paste takes. Parsing to an HTML
          // string here and calling `insertContent` instead round-trips
          // through tiptap-markdown's markdown-parsing `insertContentAt`
          // override, which re-reads those tags as markdown source and
          // (with `html: false`) escapes them into literal visible text.
          const slice = view.someProp('clipboardTextParser', (f) =>
            f(text, view.state.selection.$from, false, view),
          )
          if (!slice) return false
          event.preventDefault()
          view.dispatch(
            view.state.tr
              .replaceSelection(slice)
              .scrollIntoView()
              .setMeta('paste', true)
              .setMeta('uiEvent', 'paste'),
          )
          return true
        },
      },
      onUpdate: ({ editor }) => {
        // Only fire while in rich mode — the textarea owns the
        // markdown when in markdown mode.
        if (mode !== 'rich') return
        // Phase 4 — emit JSON for the wiki surface, markdown for every
        // other surface. The callback receives a string either way so
        // the consumer's debounce + ref bookkeeping stays uniform; only
        // the bytes differ.
        const next =
          valueFormat === 'json'
            ? JSON.stringify(editor.getJSON())
            : (editor.storage as { markdown: { getMarkdown: () => string } }).markdown.getMarkdown()
        fireChange(next)
      },
    },
    // Empty deps — never re-init the editor after first mount. `readOnly`,
    // `mode`, and `value` are all handled imperatively by effects below
    // (`editor.setEditable(...)`, `editor.commands.setContent(...)`).
    // Re-initing on `readOnly` flip caused a tippy.js unmount race in
    // BubbleMenu when CommentComposer toggled `readOnly` on submit —
    // `Failed to execute 'removeChild' on 'Node'` (Phase 2c carry-over,
    // surfaced again in Phase 3b's R-2 review). The mention `suggestion`
    // plugin is captured at first init and stable for the component
    // lifetime; consumers that need to swap members get a fresh instance
    // by remounting the parent (e.g. opening a different card).
    [],
  )

  // Apply autoFocus once the editor has mounted. We can't pass `autofocus`
  // to useEditor because that runs in the deps-empty branch above and
  // would re-init on toggle; instead we focus imperatively after mount.
  useEffect(() => {
    if (!autoFocus || !editor) return
    // The next tick — let the EditorContent render before focus to avoid
    // a Chrome bug where focus on a not-yet-laid-out contenteditable
    // sometimes silently no-ops.
    const id = window.setTimeout(() => editor.commands.focus('end'), 0)
    return () => window.clearTimeout(id)
  }, [autoFocus, editor])

  // Surface the editor instance to the parent so external
  // surfaces (chat composer's EmojiPickerPopover) can call
  // `editor.commands.insertContent(...)`. Fires once when the editor is
  // first created; subsequent renders see the same stable reference.
  // `onEditorReady` is captured in a ref so swapping the callback at
  // runtime doesn't re-fire the effect.
  const onEditorReadyRef = useRef(onEditorReady)
  useEffect(() => {
    onEditorReadyRef.current = onEditorReady
  }, [onEditorReady])
  useEffect(() => {
    if (!editor) return
    onEditorReadyRef.current?.(editor)
  }, [editor])

  // Sync external `value` changes into our state + the TipTap editor.
  // Only when the new value genuinely differs from what we already have
  // — otherwise typing mid-stream gets clobbered by a stale prop. We
  // read the latest internal `markdown` via the ref so this effect
  // doesn't re-fire on every keystroke.
  //
  // Phase 4 — `valueFormat='json'` parses the incoming value-string into
  // a ProseMirror doc and calls setContent with the parsed object;
  // markdown path stays unchanged.
  //
  // BOTH gates below are SEMANTIC, never bytewise, and that is
  // load-bearing rather than a tidy-up. A parent that echoes a saved body
  // back down (an autosave response, a query refetch, a realtime
  // invalidation) hands us a string that is guaranteed to differ
  // byte-for-byte from what the editor holds while meaning exactly the
  // same thing — see `valuesEquivalent`. Under a byte compare that
  // no-op echo used to run the whole body of this effect: it cancelled
  // the user's pending debounce (DISCARDING their last <300ms of typing,
  // which `lastEmittedRef` then made unrecoverable) and replaced the live
  // document via `setContent`, dumping the caret at the end. Zero visible
  // content change, full caret and keystroke loss. Keep the destructive
  // steps behind the same gate as the replacement.
  useEffect(() => {
    if (valuesEquivalent(value, markdownRef.current, valueFormat)) return
    // External update (e.g. the chat composer cleared `body` after
    // sending, or the parent refetched the card). Cancel any pending
    // debounced onChange — the parent's `value` is the source of truth
    // now; an in-flight debounce would replay stale typed text and
    // re-populate the editor (visible to the user as "the composer
    // didn't clear after I pressed Send").
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setMarkdown(value)
    markdownRef.current = value
    lastEmittedRef.current = value
    if (editor) {
      const current = readEditorValue(editor, valueFormat)
      if (!valuesEquivalent(current, value, valueFormat)) {
        // Snapshot BEFORE the replace — `setContent` discards the
        // selection (see `restoreSelectionAfterReplace`). `hasFocus`
        // gates the restore so we only re-seat a caret the user actually
        // had; an unfocused editor keeps ProseMirror's default.
        const hadFocus = editor.view.hasFocus()
        const { from, to } = editor.state.selection
        if (valueFormat === 'json') {
          // `resolveJsonContent` falls back to the raw string for legacy
          // markdown rows — dual-format read, same as the init path.
          editor.commands.setContent(resolveJsonContent(value, contentFormat), false)
        } else {
          editor.commands.setContent(value, false)
        }
        if (hadFocus) restoreSelectionAfterReplace(editor, from, to)
      }
    }
  }, [value, editor, valueFormat, contentFormat])

  // Flush the debounce on unmount so the parent doesn't lose the last
  // edit if the user navigates away within 300ms. Reads everything via
  // refs so the cleanup is unmount-only.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        const pending = markdownRef.current
        const cb = onChangeRef.current
        if (cb && pending !== lastEmittedRef.current) {
          lastEmittedRef.current = pending
          cb(pending)
        }
      }
    }
  }, [])

  // Keep the Placeholder extension's `placeholder` option in
  // sync with the prop. The editor mounts once (deps-empty `useEditor`),
  // so a static `placeholder` captured at init won't update when async
  // data lands (e.g. the chat composer's "Message #general" derives
  // from `useConversation` which resolves AFTER the editor mounts).
  // Imperatively patch the extension option + force a doc-noop dispatch
  // so the decoration re-renders with the new text.
  useEffect(() => {
    if (!editor) return
    const placeholderExt = editor.extensionManager.extensions.find(
      (ext) => ext.name === 'placeholder',
    ) as { options: { placeholder: string } } | undefined
    if (!placeholderExt) return
    const next = placeholder ?? ''
    if (placeholderExt.options.placeholder === next) return
    placeholderExt.options.placeholder = next
    editor.view.dispatch(editor.state.tr)
  }, [editor, placeholder])

  // Keep the editor's editable state in sync when readOnly toggles
  // without a re-init (the deps array on useEditor handles re-init,
  // this is the no-re-init fallback for when extensions are stable).
  useEffect(() => {
    if (!editor) return
    if (editor.isEditable === !readOnly) return
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  const switchMode = useCallback(() => {
    const next: 'rich' | 'markdown' = mode === 'rich' ? 'markdown' : 'rich'
    if (next === 'markdown') {
      // rich → markdown: read latest markdown out of the editor, then
      // render the textarea seeded with it.
      const md = readMarkdown(editor)
      setMarkdown(md)
    } else {
      // markdown → rich: pour the textarea's markdown back into the
      // editor and switch surfaces.
      if (editor) {
        editor.commands.setContent(markdown, false)
      }
    }
    setInternalMode(next)
    onModeChange?.(next)
  }, [mode, editor, markdown, onModeChange])

  // Controlled-mode bridge — when the parent flips `markdownMode`, mirror
  // it into internal state and run the same setContent/serialize side
  // effects the internal `switchMode` does. Without this, the parent's
  // controlled flip would update the visible surface (mode derives from
  // `markdownMode ?? internalMode`) but skip the markdown↔rich sync, so
  // the textarea would show stale text on the first rich→markdown
  // toggle.
  //
  // `markdown` + `internalMode` are read inside the effect as latest
  // snapshots, but deliberately excluded from deps — re-running on
  // every keystroke or every internal-mode flip would loop the
  // controlled-prop bridge with the internal toggle path. The effect
  // is bound to the controlled-prop transition only.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above.
  useEffect(() => {
    if (markdownMode === undefined) return
    if (markdownMode === internalMode) return
    if (markdownMode === 'markdown') {
      const md = readMarkdown(editor)
      setMarkdown(md)
    } else if (editor) {
      editor.commands.setContent(markdown, false)
    }
    setInternalMode(markdownMode)
  }, [markdownMode, editor])

  // Style props derived once.
  const minHeightPx = useMemo(() => rowToPx(minRows), [minRows])
  const maxHeightPx = useMemo(() => (maxRows ? rowToPx(maxRows) : undefined), [maxRows])

  // ---- Code-highlight loading gate ----------------------------------------
  // When the caller opts in via `enableCodeHighlight={true}`, we hold the
  // editor's content render behind a 1-tick skeleton until the dynamic
  // import resolves. Without this, the first paint would mount the
  // editor WITHOUT the lowlight extension, and TipTap's deps-empty
  // `useEditor` would never pick it up (re-init on the extension array
  // changing triggers the BubbleMenu tippy.js unmount race documented
  // on the `useEditor` call above). The visual cost is ~30-100ms of
  // pulsing-skeleton on Wiki doc page first paint; everything else
  // pre-loads with the editor.
  if (enableCodeHighlight && !codeHighlightExtension) {
    return (
      <div
        className="animate-pulse rounded-md border border-border bg-bg-elevated"
        style={{
          minHeight: minHeightPx,
          maxHeight: maxHeightPx,
        }}
        aria-busy="true"
      />
    )
  }

  // ---- Read-only render fast-path -----------------------------------------
  // Read-only never shows the toolbar, the toggle, or the textarea —
  // it's purely a rendering surface. Pure rich-mode display.
  //
  // `borderless` AND `chrome === 'flush'` both drop the bordered wrapper:
  // the Wiki doc page passes `chrome="flush"` for the editing surface,
  // and the same flushness must carry through to the read-mode render —
  // otherwise the doc page's `<article>` (no chrome) wraps the editor's
  // OWN bordered box and the reader sees a card-on-page that the
  // editing surface doesn't have. Caught from user feedback on 2026-05-18.
  //
  // The wiki surface is DELIBERATELY excluded from this fast-path. It's the
  // only surface that mounts tippy BubbleMenus (Comment + Table) AND toggles
  // read-only (the Edit/View flip). Returning a different root element type
  // here (Fragment) than the editable branch below (a `<div>`) makes React
  // tear the whole subtree down on every flip — which unmounts the tippy
  // BubbleMenus mid-flight and throws `removeChild on Node`. Routing wiki
  // through the shared editable-structured render keeps one stable root +
  // keeps the BubbleMenus mounted (their `shouldShow` hides them in view
  // mode), so the flip becomes a pure `setEditable` with no DOM teardown.
  // Non-wiki read-only surfaces (chat / card / comment view) are a pure
  // rendering surface with no view-mode comment affordance — that button is
  // wiki-only and is rendered by the shared editable-structured branch below
  // (which the wiki surface always flows through, editable or not).
  // Decided on the first render that actually reaches `<EditorContent>` —
  // the skeleton above carries none, so it must not get a vote. Writing a
  // lazily-initialized ref during render is idempotent and re-runs to the
  // same value under StrictMode's doubled render.
  const liveContentShape = resolveContentShape({ readOnly, surface, borderless, chrome })
  if (contentShapeRef.current === null) {
    contentShapeRef.current = liveContentShape
  }
  const contentShape = contentShapeRef.current

  if (import.meta.env.DEV) {
    const live = liveContentShape
    if (live !== contentShape) {
      console.warn(
        `[MarkdownEditor] props now ask for the "${live}" layout but the editor mounted as "${contentShape}", which is frozen for its lifetime. The frozen shape keeps node views rendering; the new one is being ignored. A composer must not go read-only while submitting — disable its buttons instead. A surface that genuinely needs both layouts should mount a separate editor per layout, the way EditableField does.`,
      )
    }
  }

  if (contentShape !== 'editable') {
    if (contentShape === 'readOnlyBare') {
      return <EditorContent editor={editor} />
    }
    return (
      <div
        className="rounded-md border border-border bg-bg-elevated"
        style={{
          minHeight: minHeightPx,
          maxHeight: maxHeightPx,
          overflowY: maxHeightPx ? 'auto' : 'visible',
        }}
      >
        <EditorContent editor={editor} />
      </div>
    )
  }

  // Filter bare Enter / Shift+Enter from the parent's onKeyDown — those
  // are paragraph / soft-break inside the editor and must not trigger an
  // EditableField "commit on Enter" handler. Cmd/Ctrl+Enter, Escape, and
  // every other key still bubble through.
  //
  // `forwardEnterKeys` surfaces get their Enter events via the
  // ProseMirror-level intercept in `editorProps.handleKeyDown` (which
  // runs BEFORE TipTap's Enter keymap — see the comment there), so the
  // wrapper filters ALL Enter events for them: letting Enter through
  // here as well would fire the parent's handler twice per keypress
  // (once at the PM layer, once on bubble) and double-submit. Enter
  // pressed outside the contenteditable (e.g. a focused toolbar button)
  // never reaches the PM intercept and correctly activates the button
  // instead of sending.
  //
  // We deliberately do NOT bail on `event.defaultPrevented`
  // here. ProseMirror's default Enter keymap (insert paragraph) ALWAYS
  // calls `event.preventDefault()`, so the flag can't distinguish
  // "suggestion popover ate it" from "ProseMirror inserted a paragraph".
  // The chat composer instead tracks suggestion-popover state via the
  // suggestion plugin's `onPopoverStateChange` callback and gates submit
  // there. See `ChatComposer.tsx` → `mentionPopoverOpenRef`.
  const handleWrapperKeyDown = onKeyDown
    ? (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
          if (forwardEnterKeys) return
          if (!event.metaKey && !event.ctrlKey) return
        }
        onKeyDown(event)
      }
    : undefined

  // ---- Editable render ----------------------------------------------------
  //
  // `chrome === 'flush'` (Wiki full-page editor) sheds the bordered +
  // elevated wrapper entirely — no border, no shadow, no card surface,
  // no focus-ring. The editor sits flat on the page bg and lets the
  // page (title input above, page gutter to the sides) own the
  // chrome. Confluence / Notion look. The `card` variant keeps the
  // legacy `card` styling — every chat / comment / card-description
  // call-site still gets the contained box without changes.
  const wrapperClass =
    chrome === 'flush'
      ? 'flex flex-col'
      : 'flex flex-col rounded-md border border-border-strong bg-bg-elevated shadow-sm focus-within:border-fg focus-within:ring-1 focus-within:ring-fg'

  return (
    <div className={wrapperClass} onKeyDown={handleWrapperKeyDown}>
      {/*
       * Both surfaces stay mounted — only their visibility toggles. If we
       * conditionally rendered them, the TipTap view DOM would tear down
       * on every mode flip, and BubbleMenu's tippy popper (which holds
       * a reference to the editor view) would crash with
       * `removeChild on Node` when the unmount races the editor-view
       * destruction. Keeping both mounted means tippy always has a valid
       * reference and CSS does the swap.
       */}
      <div className={mode === 'rich' ? 'flex flex-col' : 'hidden'} aria-hidden={mode !== 'rich'}>
        {!readOnly && enableFormattingToolbar && showInlineToolbar ? (
          <EditorToolbar
            editor={editor}
            preset={toolbarPreset ?? defaultPresetForSurface(surface)}
            surface={surface}
            chrome={chrome}
            enableTables={enableTables}
            attachFiles={onAttachFiles}
            t={t}
          />
        ) : null}
        {/*
         * Make the empty space below the ProseMirror clickable.
         * ProseMirror sizes to its content; with minRows=3 the wrapper is
         * ~88px tall but the rendered `<p>` is ~24px, leaving dead space
         * the user expects to click into. `onMouseDown` on the wrapper
         * (only when the target IS the wrapper, not a child) focuses the
         * editor at end-of-document. Same trick legacy `Composer.jsx`
         * uses on its outer surface.
         */}
        <div
          // In read-only (the wiki view-mode flip routes here — see the
          // fast-path comment above), drop the click-to-focus affordance and
          // the tall min-height so the view reads snug, matching the old
          // Fragment fast-path. The element itself stays put in either state
          // so `<EditorContent>`'s ProseMirror view is never remounted (a
          // remount would tear down the tippy BubbleMenus → removeChild crash).
          className={readOnly ? 'overflow-y-auto' : 'cursor-text overflow-y-auto'}
          style={{ minHeight: readOnly ? undefined : minHeightPx, maxHeight: maxHeightPx }}
          onMouseDown={
            readOnly
              ? undefined
              : (event) => {
                  if (event.target !== event.currentTarget) return
                  if (!editor) return
                  event.preventDefault()
                  editor.commands.focus('end')
                }
          }
        >
          <EditorContent editor={editor} />
        </div>
        {readOnly ? (
          <ViewModeCommentButton editor={editor} enabled={surface === 'wiki' && canComment} />
        ) : null}
      </div>

      <textarea
        aria-label={ariaLabel}
        aria-hidden={mode !== 'markdown'}
        value={markdown}
        placeholder={placeholder}
        onChange={(e) => fireChange(e.target.value)}
        tabIndex={mode === 'markdown' ? 0 : -1}
        // When `chrome === 'flush'` the textarea inherits the page bg
        // (no card backdrop) and drops its horizontal padding so the
        // markdown source aligns with the title input above. The card
        // variant keeps its bg-bg-elevated fill so the textarea reads
        // as a contained surface inside the bordered wrapper.
        className={
          mode === 'markdown'
            ? chrome === 'flush'
              ? 'block w-full resize-none border-0 bg-transparent px-0 py-2 font-mono text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-0'
              : 'block w-full resize-none rounded-md border-0 bg-bg-elevated px-3 py-2 font-mono text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-0'
            : 'hidden'
        }
        style={{ minHeight: minHeightPx, maxHeight: maxHeightPx }}
      />

      {/*
       * BubbleMenu portals through tippy.js to document.body. It must
       * stay mounted as long as the editor instance exists; remounting
       * it on mode flip caused the removeChild crash mentioned above.
       * Its own `shouldShow` predicate (`editor.isActive('table')`)
       * keeps it invisible when the cursor isn't inside a table.
       */}
      {editor && enableTables && bubbleMenuPreset !== 'none' ? (
        <TableBubbleMenu editor={editor} t={t} />
      ) : null}
      {editor && enableTables && bubbleMenuPreset !== 'none' ? (
        <TableFooterPopover editor={editor} t={t} />
      ) : null}
      {editor && (bubbleMenuPreset === 'comment' || surface === 'wiki') ? (
        <CommentBubbleMenu editor={editor} t={t} surface={surface} canComment={canComment} />
      ) : null}

      {/*
       * The custom-panel picker moved IN-NodeView on 2026-05-29 (see
       * `CustomPanelView` → `<CustomPanelPicker>`). The earlier tippy-
       * based bubble menu re-positioned every transaction as the
       * panel grew with typed content, visually reading as "the menu
       * is moving while I type." The inline footer keeps the picker
       * fixed to the panel's bottom edge as a real layout child.
       */}

      {/*
       * The raw-markdown textarea mode is suppressed on JSON-persisting
       * surfaces: the textarea edits MARKDOWN source and its onChange
       * would emit markdown bytes into a pipeline whose consumers expect
       * a serialized doc — silently corrupting the save. Markdown is a
       * derived projection on these surfaces (ADR-039), not an editable
       * source; the wiki made the same call when it flipped to JSON.
       */}
      {enableModeToggle && chrome !== 'flush' && valueFormat !== 'json' && (
        <div className="flex items-center justify-end border-t border-border px-2 py-1">
          <button
            type="button"
            onClick={switchMode}
            className="text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded px-1"
            aria-label={
              mode === 'rich'
                ? t('editor.modeToggle.switchToMarkdown')
                : t('editor.modeToggle.switchToRichText')
            }
          >
            {mode === 'rich'
              ? t('editor.modeToggle.labelToMarkdown')
              : t('editor.modeToggle.labelToRichText')}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table bubble menu — appears only when the cursor is inside a table.
// Every command here round-trips through GFM markdown: structural ops
// (add/delete row/col, delete table) leave the persisted shape unchanged.
// Per-column alignment is a follow-up — it needs `textAlign` attributes
// on TableCell/TableHeader plus a verified round-trip spike case.
// ---------------------------------------------------------------------------

interface TableBubbleMenuProps {
  editor: NonNullable<ReturnType<typeof useEditor>>
  t: TFunction<'common'>
}

function TableBubbleMenu({ editor, t }: TableBubbleMenuProps) {
  // Read the active cell's `textAlign` attribute on every render so the
  // alignment buttons reflect the live state. Both `tableCell` and
  // `tableHeader` carry the attribute (registered identically via the
  // WikiTable* extensions in `nodes/table/table-extensions.ts`).
  const cellAttrs = editor.getAttributes('tableCell')
  const headerAttrs = editor.getAttributes('tableHeader')
  const activeAlign: 'left' | 'center' | 'right' | null =
    (cellAttrs.textAlign as 'left' | 'center' | 'right' | null) ??
    (headerAttrs.textAlign as 'left' | 'center' | 'right' | null) ??
    null

  // Toggle the attribute on the cell type the cursor is in. Clicking
  // the already-active alignment clears it (back to the default
  // left-aligned cell render). `updateAttributes` resolves the
  // nearest matching ancestor — for headers we target `tableHeader`,
  // everywhere else `tableCell`.
  const setAlign = (align: 'left' | 'center' | 'right') => {
    const next = activeAlign === align ? null : align
    const target = editor.isActive('tableHeader') ? 'tableHeader' : 'tableCell'
    editor.chain().focus().updateAttributes(target, { textAlign: next }).run()
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        placement: 'top',
        duration: 100,
        // Anchor to the TABLE's top edge, not the selection. Tippy's default
        // reference is the caret, so the menu chased it from cell to cell and
        // sat directly over the line being typed — and, placed above, over the
        // line above that. Anchoring to stable geometry is what every mature
        // editor does; a fuller redesign moves these controls into gutters.
        getReferenceClientRect: () => tableMenuReferenceRect(editor, 'top'),
        offset: [0, 8],
        popperOptions: tableMenuPopperOptions(editor),
      }}
      // `isEditable` is NOT redundant. TipTap's DEFAULT shouldShow checks it,
      // but passing a custom predicate REPLACES that default outright — so
      // without this the row/column controls appeared on a read-only doc, and
      // the commands behind them still ran: `editable` gates user input, not
      // transactions, so a reader could restructure a table.
      shouldShow={({ editor: e }) =>
        e.isEditable && e.isActive('table') && isTableEdgeInView(e, 'top')
      }
      className="flex items-center gap-0.5 rounded-md border border-border bg-bg-elevated p-1 shadow-md"
    >
      <ToolbarButton
        label={t('editor.tableMenu.alignLeft')}
        onClick={() => setAlign('left')}
        active={activeAlign === 'left'}
      >
        <AlignLeftIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.alignCenter')}
        onClick={() => setAlign('center')}
        active={activeAlign === 'center'}
      >
        <AlignCenterIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.alignRight')}
        onClick={() => setAlign('right')}
        active={activeAlign === 'right'}
      >
        <AlignRightIcon />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label={t('editor.tableMenu.addRowAbove')}
        onClick={() => editor.chain().focus().addRowBefore().run()}
      >
        <RowAboveIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.addRowBelow')}
        onClick={() => editor.chain().focus().addRowAfter().run()}
      >
        <RowBelowIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.deleteRow')}
        onClick={() => editor.chain().focus().deleteRow().run()}
      >
        <RowDeleteIcon />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label={t('editor.tableMenu.addColumnLeft')}
        onClick={() => editor.chain().focus().addColumnBefore().run()}
      >
        <ColLeftIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.addColumnRight')}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
      >
        <ColRightIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.tableMenu.deleteColumn')}
        onClick={() => editor.chain().focus().deleteColumn().run()}
      >
        <ColDeleteIcon />
      </ToolbarButton>
      {/*
       * Delete-table is now in the <TableFooterPopover> (bottom-of-table
       * menu) alongside the header-bg picker — table-LEVEL ops live
       * together there. The cell bubble menu keeps only cell + row +
       * column structural ops.
       */}
    </BubbleMenu>
  )
}

// ---------------------------------------------------------------------------
// Comment bubble menu — slim 5-button selection toolbar for short-form
// prose surfaces (wiki comments). Shows only when a non-empty text
// selection exists. The link button opens the canonical LinkEditorPopover
// via the same `wiki:open-link-popover` window event the Toolbar bridges
// — keeps the comment surface aligned with every other link author flow.
// ---------------------------------------------------------------------------

interface CommentBubbleMenuProps {
  editor: NonNullable<ReturnType<typeof useEditor>>
  t: TFunction<'common'>
  /** Caller surface tag — the Comment button is wiki-only. */
  surface?: EditorSurface
  /**
   * Whether the doc is commentable for this user (wiki only). Gates the
   * trailing "Comment" button — hidden for Reader / view-only users.
   */
  canComment?: boolean
}

function CommentBubbleMenu({
  editor,
  t,
  surface = 'card',
  canComment = false,
}: CommentBubbleMenuProps) {
  // The "Comment on selection" affordance is wiki-only and gated on the
  // resolved per-doc `canComment` permission. `comments.*` keys live in
  // the `wiki.json` namespace, so read them off a dedicated `t` instance.
  const { t: tWiki } = useTranslation('wiki')
  const showCommentButton =
    surface === 'wiki' && canComment && captureSelectionAnchor(editor) !== null

  const openLink = () => {
    const { from, to } = editor.state.selection
    const selectedText = from === to ? '' : editor.state.doc.textBetween(from, to, ' ', ' ')
    window.dispatchEvent(
      new CustomEvent('wiki:open-link-popover', {
        detail: {
          source: 'slash-menu',
          selectionFrom: from,
          selectionTo: to,
          selectedText,
        },
      }),
    )
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ placement: 'top', duration: 100 }}
      shouldShow={({ editor: e }) =>
        shouldShowFormattingMenu(e.state.selection, e.view.hasFocus(), e.isEditable)
      }
      className="flex items-center gap-0.5 rounded-md border border-border bg-bg-elevated p-1 shadow-md"
    >
      <ToolbarButton
        label={t('editor.toolbar.bold')}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        tone="subtle"
      >
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.toolbar.italic')}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        tone="subtle"
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.toolbar.underline')}
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        tone="subtle"
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.toolbar.strikethrough')}
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        tone="subtle"
      >
        <span className="line-through">S</span>
      </ToolbarButton>
      <ToolbarButton
        label={editor.isActive('link') ? t('editor.toolbar.linkEdit') : t('editor.toolbar.link')}
        active={editor.isActive('link')}
        onClick={openLink}
        tone="subtle"
      >
        <LinkIcon />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.toolbar.inlineCode')}
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
        tone="subtle"
      >
        <span className="font-mono text-[11px]">{'</>'}</span>
      </ToolbarButton>
      {/*
       * Quote is the one BLOCK format in an otherwise inline-mark bubble,
       * and it's here because this bubble is the ONLY formatting surface
       * the comment composers have — the wiki rail thread, the page
       * comments below a doc and the inline anchored composer all render
       * the slim 4-button action bar, not the full toolbar. Without it,
       * quoting in a comment means knowing to type `> `, which is the
       * one place quoting is most useful. Toggling on a text selection
       * wraps the blocks the selection spans, same as the toolbar.
       */}
      <ToolbarButton
        label={t('editor.toolbar.quote')}
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        tone="subtle"
      >
        <BlockquoteIcon />
      </ToolbarButton>
      {showCommentButton ? (
        <>
          <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border" />
          <ToolbarButton
            label={tWiki('comments.inline.bubbleButtonAria')}
            active={false}
            onClick={() => dispatchAddInlineComment(editor)}
            tone="subtle"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
          </ToolbarButton>
        </>
      ) : null}
    </BubbleMenu>
  )
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------
